import { useState } from "react";
import { ChevronDown, Search, Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <nav className="absolute top-0 left-0 right-0 z-50 pt-2">
      {/* Top Bar - Hidden on mobile */}
      <div className="bg-transparent hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-12 lg:h-10 xl:h-12 [@media(min-width:1280px)_and_(max-height:650px)]:h-10">
            <div className="flex items-center space-x-4 lg:space-x-5 xl:space-x-8 text-sm lg:text-[13px] xl:text-base [@media(min-width:1280px)_and_(max-height:650px)]:space-x-4 [@media(min-width:1280px)_and_(max-height:650px)]:text-[14.5px]">
              <div className="text-white flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Storgatan 12, 123 45 Stockholm</span>
              </div>
              
              <a href="mailto:info@renglans.se" className="text-white hover:text-red-400 transition-colors duration-300 flex items-center gap-1.5 group">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 group-hover:text-red-400 transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>info@renglans.se</span>
              </a>
              
              <a href="tel:0101234567" className="text-white hover:text-red-400 transition-colors duration-300 flex items-center gap-1.5 group">
                <Phone className="w-5 h-5 flex-shrink-0 group-hover:text-red-400 transition-colors duration-300" />
                <span>010-123 45 67</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-transparent [@media(min-width:1280px)_and_(max-height:650px)]:-mt-1 [@media(min-width:750px)]:border-b-0 border-b-[1px] border-white/10 [@media(min-width:750px)]:mx-0 mx-4 pb-[6px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-12 xl:h-16 [@media(min-width:1280px)_and_(max-height:650px)]:h-12">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <a href="/" className="flex items-center hover:opacity-90 transition-opacity -mt-6 md:-mt-8">
                <div className="h-40 md:h-40 lg:h-48 xl:h-60 flex items-center mt-2 md:mt-0">
                  <img 
                    src="/renglans2.webp" 
                    alt="RenGlans Städtjänster" 
                    className="h-full w-auto max-h-40 md:max-h-40 lg:max-h-60 transition-all duration-300 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/renglans2.webp'; // Fallback in case of error
                    }}
                  />
                </div>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex flex-1 justify-center lg:items-center lg:space-x-4 xl:space-x-6 [@media(min-width:1024px)_and_(max-width:1200px)]:space-x-3 lg:mt-1.5 xl:mt-2">
              <a href="/" className="text-white hover:text-red-300 transition-colors duration-200 font-['Raleway'] font-semibold text-[16px] lg:text-[17px] xl:text-[18px] py-2 px-2.5 xl:px-3 border-b-2 border-transparent hover:border-white/80">
                Hem
              </a>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 text-white hover:text-red-300 transition-colors duration-200 font-['Raleway'] font-semibold text-[16px] lg:text-[17px] xl:text-[18px] py-2 px-2.5 xl:px-3 border-b-2 border-transparent hover:border-white/80 focus:outline-none">
                    Tjänster
                    <ChevronDown className="w-4 h-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  className="bg-white border border-gray-200 shadow-lg rounded-lg py-2 min-w-[220px] z-50"
                  sideOffset={8}
                  align="start"
                >
                  {[
                    { href: "/hemstadning", label: "Hemstädning" },
                    { href: "#office", label: "Kontorsstädning" },
                    { href: "#moving", label: "Flyttstädning" },
                    { href: "#deep", label: "Storstädning" },
                  ].map((item) => (
                    <DropdownMenuItem key={item.href} className="p-0 focus:bg-transparent">
                      <a
                        href={item.href}
                        className="block w-full px-4 py-2 text-gray-800 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 text-sm lg:text-[15px] xl:text-base font-['Raleway'] font-semibold text-left"
                        aria-label={item.href === "/hemstadning" ? "Hemstädning - Städning i hemmet" : item.label}
                      >
                        {item.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <a href="/galleri" className="text-white hover:text-red-300 transition-colors duration-200 font-['Raleway'] font-semibold text-[16px] lg:text-[17px] xl:text-[18px] py-2 px-2.5 xl:px-3 border-b-2 border-transparent hover:border-white/80">
                Galleri
              </a>

              <a href="/om-oss" className="text-white hover:text-red-300 transition-colors duration-200 font-['Raleway'] font-semibold text-[16px] lg:text-[17px] xl:text-[18px] py-2 px-2.5 xl:px-3 border-b-2 border-transparent hover:border-white/80">
                Om oss
              </a>
            </div>

            <div className="hidden lg:flex lg:items-center lg:justify-end">
              <a href="/kontakt" className="inline-block">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-['Raleway'] font-bold text-base lg:text-base xl:text-lg px-4 lg:px-5 xl:px-6 py-[12px] xl:py-[14px] rounded-md transition-colors border-2 border-white hover:border-white/80 ml-4 whitespace-nowrap h-auto leading-none -my-0.5">
                  <span className="scale-100 inline-block">Kontakta Oss</span>
                </Button>
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white hover:bg-white/10"
                aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[hsl(var(--nav-bg))] border-t border-white/10">
          <div className="px-4 pt-2 pb-3 space-y-1">
            <a href="/" className="block py-4 text-white hover:text-red-400 transition-colors duration-300 text-lg">
              Hem
            </a>
            
            {/* Städtjänster Dropdown */}
            <div className="py-1">
              <button 
                onClick={() => setServicesOpen(!servicesOpen)}
                className="w-full flex items-center justify-between py-4 text-white hover:text-red-400 transition-colors duration-300 text-lg"
              >
                <span>Tjänster</span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {servicesOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  <a href="/hemstadning" className="block py-3 text-white/90 hover:text-red-400 transition-colors duration-300 border-l-2 border-white/10 pl-4" aria-label="Hemstädning - Städning i hemmet">
                    Hemstädning
                  </a>
                  <a href="#" className="block py-3 text-white/90 hover:text-red-400 transition-colors duration-300 border-l-2 border-white/10 pl-4">
                    Kontorsstädning
                  </a>
                  <a href="#" className="block py-3 text-white/90 hover:text-red-400 transition-colors duration-300 border-l-2 border-white/10 pl-4">
                    Flyttstädning
                  </a>
                  <a href="#" className="block py-3 text-white/90 hover:text-red-400 transition-colors duration-300 border-l-2 border-white/10 pl-4">
                    Byggstädning
                  </a>
                </div>
              )}
            </div>
            
            <a href="/galleri" className="block py-4 text-white hover:text-red-400 transition-colors duration-300 text-lg">
              Galleri
            </a>
            
            <a href="/om-oss" className="block py-4 text-white hover:text-red-400 transition-colors duration-300 text-lg">
              Om oss
            </a>
            
            <a href="/kontakt" className="block py-4 text-white hover:text-red-400 transition-colors duration-300 text-lg font-semibold">
              Kontakta Oss
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
